import { useCallback, memo, useState, useEffect, SyntheticEvent } from 'react';
import VideoSidebar from '@picsart/rc/old/VideoSidebar';
import { useDispatch, useSelector } from 'react-redux';
import { importTemplateAction, getTemplateList } from 'modules/template';
import { TemplateConfirmModal } from './templatesTool/TemplateConfirmModal';
import { getScenes } from 'modules/scenes';
import { SceneType } from 'types/Scene';
import { TemplatePreviewModal } from './templatesTool/TemplatePreviewModal';
import { TemplateListItem, License } from 'types/templateTypes';
import { TargetSettingsAspectRatioEnum } from 'constants/ratioFormats';
import analytics from 'utils/analytics';
import { applyTemplateSizeFilter, getTemplateSizeFilter } from 'modules/filters';
import { IFilterData, UsageType } from '@picsart/rc/types';

const SizeFilterEnum = {
  ...TargetSettingsAspectRatioEnum,
  All: 'all'
};

export const filterData: { id: string; size: string }[] = [
  {
    id: 'all_templates',
    size: 'all'
  },
  {
    id: 'youtube_cover',
    size: '16:9'
  },
  {
    id: 'insta_Portrait',
    size: '4:5'
  },
  {
    id: 'insta_story',
    size: '9:16'
  },
  {
    id: 'insta_square',
    size: '1:1'
  },
  {
    id: 'fb_square',
    size: '4:5'
  },
  {
    id: 'fb_post',
    size: '1:1'
  },
  {
    id: 'fb_story',
    size: '9:16'
  },
  {
    id: 'tik_tok',
    size: '9:16'
  },
  {
    id: 'snapchat',
    size: '9:16'
  },
  {
    id: 'twitter_portrait',
    size: '4:5'
  }
];

// TODO: сделать через reducer
export const TemplatesTool = memo(() => {
  const dispatch = useDispatch();
  const selectedTemplateFilter = useSelector(getTemplateSizeFilter);
  const selectedFilter = filterData.find(filter => filter.id === selectedTemplateFilter);
  const [templateId, setTemplateId] = useState<string>(null);
  const [previewUrl, setPreviewUrl] = useState<string>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const scenes = useSelector(getScenes);
  const originTemplatesList = useSelector(getTemplateList);
  const [filteredTemplatesList, setFilteredTemplatesList] = useState<TemplateListItem[]>(
    originTemplatesList
  );
  const [queryFilter, setQueryFilter] = useState<string>('');
  const [sizeFilter, setSizeFilter] = useState(selectedFilter.size);
  const [licenseFilter, setLicenseFilter] = useState(UsageType.all);

  const closePreviewHandler = useCallback(() => {
    setPreviewUrl(null);
    setTemplateId(null);
  }, []);

  const applyPreviewHandler = useCallback(() => {
    if (scenes.length === 1 && scenes[0].type === SceneType.EMPTY) {
      // empty project -> applyTemplate
      analytics.templateAction({ item_id: templateId });
      dispatch(importTemplateAction(templateId));
      setTemplateId(null);
    } else {
      // not empty project -> confirmModal
      setIsConfirmOpen(true);
    }

    setPreviewUrl(null);
  }, [templateId, scenes]);

  const closeConfirmHandler = useCallback(() => {
    setIsConfirmOpen(false);
    setTemplateId(null);
  }, []);

  const applyConfirmHandler = useCallback(() => {
    analytics.templateAction({ item_id: templateId });
    dispatch(importTemplateAction(templateId));
    setTemplateId(null);

    setIsConfirmOpen(false);
  }, [templateId]);

  const filterByUsageType = (typeId: UsageType) => {
    setLicenseFilter(typeId);
  };

  const handleSearchVideos = useCallback((_e: SyntheticEvent, query: string) => {
    setQueryFilter(query);
  }, []);

  const filterBySize = useCallback((_e: React.MouseEvent, data: IFilterData) => {
    dispatch(applyTemplateSizeFilter(data.id));
  }, []);

  const handleSelectVideo = (_e: React.MouseEvent, id: string) => {
    setTemplateId(id);
  };

  useEffect(() => {
    const selectedFilter = filterData.find(filter => filter.id === selectedTemplateFilter);
    setSizeFilter(selectedFilter.size);
  }, [selectedTemplateFilter]);

  useEffect(() => {
    setFilteredTemplatesList(originTemplatesList);
  }, [originTemplatesList]);

  useEffect(() => {
    if (!templateId || !originTemplatesList) return;

    const template = originTemplatesList.find(item => item.id === templateId);
    setPreviewUrl(template?.preview_url);
  }, [templateId, originTemplatesList]);

  useEffect(() => {
    let filtered = originTemplatesList;
    const rgx = new RegExp(queryFilter, 'i');
    if (queryFilter) {
      filtered = filtered.filter(item => {
        return item.tags.some(tag => tag.match(rgx));
      });
    }

    if (sizeFilter !== SizeFilterEnum.All) {
      filtered = filtered.filter(item => item.format === sizeFilter);
    }

    filtered = filtered.filter(item =>
      licenseFilter === UsageType.all
        ? [License.Free, License.Premium].includes(item.license)
        : item.license === License.Fte
    );

    setFilteredTemplatesList(filtered);
  }, [originTemplatesList, queryFilter, sizeFilter, licenseFilter]);

  return (
    <div
      sx={{
        height: '100%'
      }}
    >
      <VideoSidebar
        loading={false}
        data={filteredTemplatesList}
        handleSelectVideo={handleSelectVideo}
        handleSearchVideos={handleSearchVideos}
        filterByUsageType={filterByUsageType}
        filterBySize={filterBySize}
        currentSize={selectedTemplateFilter}
      />
      <TemplatePreviewModal
        previewUrl={previewUrl}
        opened={!!previewUrl}
        closeHandler={closePreviewHandler}
        applyHandler={applyPreviewHandler}
      />
      <TemplateConfirmModal
        opened={isConfirmOpen}
        closeHandler={closeConfirmHandler}
        applyHandler={applyConfirmHandler}
      />
    </div>
  );
});
